import type { VersionBroadcastMessage } from '../types/version.types';

// Глобальный флаг устаревания версии
let isVersionOutdated = false;

// BroadcastChannel для синхронизации между вкладками
let broadcastChannel: BroadcastChannel | null = null;

// Callback для обработки события устаревания версии
let onOutdatedCallback: (() => void) | null = null;

/**
 * Инициализировать BroadcastChannel для синхронизации между вкладками
 */
export function initVersionBroadcast(onOutdated?: () => void): void {
  if (typeof BroadcastChannel === 'undefined') {
    console.warn('[versionOutdated] BroadcastChannel API не поддерживается в этом браузере');
    return;
  }

  if (onOutdated) {
    onOutdatedCallback = onOutdated;
  }

  try {
    // Закрыть существующий канал если есть
    if (broadcastChannel) {
      broadcastChannel.close();
    }

    broadcastChannel = new BroadcastChannel('ab3-version-check');

    broadcastChannel.onmessage = (event: MessageEvent<VersionBroadcastMessage>) => {
      const message = event.data;

      if (message?.type === 'version-outdated') {
        console.log(
          '[versionOutdated] Получено сообщение об устаревшей версии от другой вкладки',
          { current: message.currentVersion, latest: message.latestVersion }
        );

        // Установить флаг устаревания
        isVersionOutdated = true;

        // Вызвать callback если установлен
        if (onOutdatedCallback) {
          try {
            onOutdatedCallback();
          } catch (error) {
            console.error('[versionOutdated] Ошибка в callback при устаревании версии', error);
          }
        }
      }
    };

    broadcastChannel.onmessageerror = () => {
      console.error('[versionOutdated] Ошибка декодирования сообщения BroadcastChannel');
    };
  } catch (error) {
    console.error('[versionOutdated] Не удалось инициализировать BroadcastChannel', error);
  }
}

/**
 * Уведомить все вкладки об устаревшей версии
 */
export function notifyVersionOutdated(currentVersion: string, latestVersion: string): void {
  if (!broadcastChannel) {
    console.warn('[versionOutdated] BroadcastChannel не инициализирован');
    return;
  }

  const message: VersionBroadcastMessage = {
    type: 'version-outdated',
    currentVersion,
    latestVersion,
    timestamp: Date.now(),
  };

  try {
    broadcastChannel.postMessage(message);
    console.log('[versionOutdated] Отправлено сообщение об устаревшей версии на все вкладки');
  } catch (error) {
    console.error('[versionOutdated] Не удалось отправить сообщение', error);
  }
}

/**
 * Закрыть BroadcastChannel
 */
export function closeVersionBroadcast(): void {
  if (broadcastChannel) {
    try {
      broadcastChannel.close();
    } catch (error) {
      console.error('[versionOutdated] Ошибка при закрытии BroadcastChannel', error);
    }
    broadcastChannel = null;
  }
}

/**
 * Установить флаг устаревания версии
 */
export function setVersionOutdated(value: boolean): void {
  isVersionOutdated = value;
  console.log(`[versionOutdated] Флаг устаревания установлен: ${value}`);
}

/**
 * Получить статус устаревания версии
 */
export function getVersionOutdated(): boolean {
  return isVersionOutdated;
}

/**
 * Остановить все периодические задачи
 * Эта функция будет вызываться при обнаружении устаревшей версии
 */
export function stopAllPeriodicTasks(): void {
  console.log('[versionOutdated] Останавливаем все периодические задачи...');

  // Установить флаг
  setVersionOutdated(true);

  // Примечание: очистка таймеров выполняется через проверку флага в каждом сервисе
  // Грубая очистка всех таймеров может привести к проблемам с другой логикой приложения

  console.log('[versionOutdated] Все периодические задачи остановлены');
}
